import { gql } from '@apollo/client';

// 企業情報を取得するクエリ
export const GET_COMPANY = gql`
  query GetCompany($companyId: ID!) {
    getCompany(companyId: $companyId) {
      companyId
      name
      contractStartDate
      contractEndDate
      maxUsers
      contactInfo
      notes
      ssoSecret
      usageCount
      deletable
      createdAt
      updatedAt
    }
  }
`;

// 企業一覧を取得するクエリ（SYSTEM_ADMIN権限のみ）
export const LIST_COMPANIES = gql`
  query ListCompanies($limit: Int, $nextToken: String) {
    listCompanies(limit: $limit, nextToken: $nextToken) {
      items {
        companyId
        name
        contractStartDate
        contractEndDate
        maxUsers
        contactInfo
        notes
        usageCount
        deletable
        ssoSecret
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ユーザー一覧を取得するクエリ
export const LIST_USERS = gql`
  query ListUsers($companyId: ID!, $limit: Int, $nextToken: String) {
    listUsers(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        userId
        givenUserId
        email
        displayName
        companyId
        department
        departments
        managementScope
        profileId
        lastLoginDate
        disable
        locked
        userRole
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ユーザーを取得するクエリ
export const GET_USER = gql`
  query GetUser($userId: ID!) {
    getUser(userId: $userId) {
      userId
      givenUserId
      email
      displayName
      companyId
      department
      departments
      managementScope
      profileId
      profile {
        profileId
        name
        description
        permissions {
          userMgmt {
            view { canView canCsvDownload }
            edit { canCreateNew canCsvImport canBulkUpdate canUpdate canDelete canChangePassword }
          }
          scenarioMgmt {
            view { canView canCsvDownload }
            edit { canCreateNew canCsvImport canUpdate canDelete canCustomScenarioUpdate }
          }
          analytics {
            view { canView canCsvDownload }
          }
          profileMgmt {
            view { canView }
            edit { canCreateNew canUpdate canDelete }
          }
        }
      }
      lastLoginDate
      disable
      locked
      userRole
      createdAt
      updatedAt
    }
  }
`;

// プロファイル一覧を取得するクエリ
export const LIST_PROFILES = gql`
  query ListProfiles($companyId: ID!, $limit: Int, $nextToken: String) {
    listProfiles(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        profileId
        companyId
        name
        description
        permissions {
          userMgmt {
            view { canView canCsvDownload }
            edit { canCreateNew canCsvImport canBulkUpdate canUpdate canDelete canChangePassword }
          }
          scenarioMgmt {
            view { canView canCsvDownload }
            edit { canCreateNew canCsvImport canUpdate canDelete canCustomScenarioUpdate }
          }
          analytics {
            view { canView canCsvDownload }
          }
          profileMgmt {
            view { canView }
            edit { canCreateNew canUpdate canDelete }
          }
        }
        createdAt
        updatedAt
        createdBy
      }
      nextToken
    }
  }
`;

// 単一プロファイル取得
export const GET_PROFILE = gql`
  query GetProfile($profileId: ID!) {
    getProfile(profileId: $profileId) {
      profileId
      companyId
      name
      description
      permissions {
        userMgmt {
          view { canView canCsvDownload }
          edit { canCreateNew canCsvImport canBulkUpdate canUpdate canDelete canChangePassword }
        }
        scenarioMgmt {
          view { canView canCsvDownload }
          edit { canCreateNew canCsvImport canUpdate canDelete canCustomScenarioUpdate }
        }
        analytics {
          view { canView canCsvDownload }
        }
        profileMgmt {
          view { canView }
          edit { canCreateNew canUpdate canDelete }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

// シナリオ一覧を取得するクエリ
export const LIST_SCENARIOS = gql`
  query ListScenarios($companyId: ID!, $limit: Int, $nextToken: String) {
    listScenarios(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        scenarioId
        companyId
        department
        title
        description
        goal
        estimatedTime
        situation
        targetName
        targetGender
        targetAge
        targetLanguage
        tags
        isActive
        usageCount
        displayOrder
        createdAt
        updatedAt
        createdBy
        materials {
          materialId
          name
          type
          pdfPath
          extractionStatus
        }
        showMaterialsOnOrientation
        situationClosed
      }
      nextToken
    }
  }
`;

// 評価を取得するクエリ
export const GET_EVALUATION = gql`
  query GetEvaluation($evaluationId: ID!) {
    getEvaluation(evaluationId: $evaluationId) {
      evaluationId
      sessionId
      userId
      scenarioId
      companyId
      department
      results
      resultPdfPath
      createdAt
      updatedAt
    }
  }
`;

// 評価一覧を取得するクエリ
export const LIST_EVALUATIONS_BY_COMPANY = gql`
  query ListEvaluationsByCompany($companyId: ID!, $limit: Int, $nextToken: String) {
    listEvaluationsByCompany(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        evaluationId
        sessionId
        userId
        scenarioId
        companyId
        department
        userDepartments
        results
        resultsSummary
        resultPdfPath
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ユーザーIDで評価一覧を取得するクエリ（該当ユーザーの履歴のみを効率的に取得）
export const LIST_EVALUATIONS_BY_USER = gql`
  query ListEvaluationsByUser($userId: ID!, $limit: Int, $nextToken: String) {
    listEvaluationsByUser(userId: $userId, limit: $limit, nextToken: $nextToken) {
      items {
        evaluationId
        sessionId
        userId
        scenarioId
        companyId
        department
        userDepartments
        results
        resultsSummary
        resultPdfPath
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ユーザーの使用履歴一覧を取得するクエリ
export const LIST_USAGES_BY_COMPANY = gql`
  query ListUsagesByCompany($companyId: ID!, $limit: Int, $nextToken: String) {
    listUsagesByCompany(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        usageId
        companyId
        department
        userDepartments
        userId
        userDisplayName
        scenarioId
        scenarioTitle
        lastEvaluateDate
        usageCount
        lastEvaluationId
        lastEvaluationSummary
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// ユーザーのログイン履歴を取得するクエリ
export const LIST_LOGIN_LOGS = gql`
  query ListLoginLogs($userId: ID!, $limit: Int, $nextToken: String) {
    listLoginLogs(userId: $userId, limit: $limit, nextToken: $nextToken) {
      items {
        logId
        userId
        companyId
        timestamp
        clientId
        createdAt
      }
      nextToken
    }
  }
`;

// 会社のログイン履歴を取得するクエリ
export const LIST_LOGIN_LOGS_BY_COMPANY = gql`
  query ListLoginLogsByCompany($companyId: ID!, $limit: Int, $nextToken: String) {
    listLoginLogsByCompany(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        logId
        userId
        companyId
        timestamp
        clientId
        createdAt
      }
      nextToken
    }
  }
`;

// ユーザーのログイン回数を取得するクエリ
export const COUNT_LOGIN_LOGS_BY_USER = gql`
  query CountLoginLogsByUser($userId: ID!, $startDate: String, $endDate: String) {
    countLoginLogsByUser(userId: $userId, startDate: $startDate, endDate: $endDate)
  }
`;

// ユーザーごとのシナリオ評価回数を取得するクエリ
export const COUNT_EVALUATIONS_BY_USER = gql`
  query CountEvaluationsByUser($userId: ID!, $scenarioId: ID!) {
    countEvaluationsByUser(userId: $userId, scenarioId: $scenarioId)
  }
`;

// ユーザーごとの当月シナリオ評価回数を取得するクエリ
export const COUNT_EVALUATIONS_BY_MONTH = gql`
  query CountEvaluationsByMonth($userId: ID!) {
    countEvaluationsByMonth(userId: $userId)
  }
`;

// シナリオを取得するクエリ
export const GET_SCENARIO = gql`
  query GetScenario($scenarioId: ID!) {
    getScenario(scenarioId: $scenarioId) {
      scenarioId
      companyId
      department
      title
      description
      goal
      estimatedTime
      situation
      targetName
      targetGender
      targetAge
      targetLanguage
      tags
      isActive
      usageCount
      createdAt
      updatedAt
      createdBy
      materials {
        materialId
        name
        type
        pdfPath
        extractionStatus
      }
      showMaterialsOnOrientation
      situationClosed
    }
  }
`;

// セッションを取得するクエリ
export const GET_SESSION = gql`
  query GetSession($sessionId: ID!) {
    getSession(sessionId: $sessionId) {
      sessionId
      userId
      scenarioId
      companyId
      department
      startTime
      endTime
      status
      characterSettings
      aiGuide
      aiSubtitleDisplay
      createdAt
      updatedAt
      createdBy
    }
  }
`;

// セッションIDに基づいて評価を取得するクエリ
export const GET_EVALUATION_BY_SESSION = gql`
  query GetEvaluationBySession($sessionId: ID!) {
    getEvaluationBySession(sessionId: $sessionId) {
      evaluationId
      sessionId
      userId
      scenarioId
      companyId
      department
      results
      resultPdfPath
      createdAt
      updatedAt
    }
  }
`;

// セッションIDに基づいて会話履歴を取得するクエリ
export const LIST_CONVERSATIONS = gql`
  query ListConversations($sessionId: ID!, $limit: Int, $nextToken: String) {
    listConversations(sessionId: $sessionId, limit: $limit, nextToken: $nextToken) {
      items {
        conversationId
        sessionId
        counter
        userContent
        aiContent
        aiParams
        aiGuide
        status
        userAudioPath
        aiAudioPath
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// 状況設定テンプレート一覧を取得するクエリ
export const LIST_SITUATION_TEMPLATES = gql`
  query ListSituationTemplates($limit: Int, $nextToken: String) {
    listSituationTemplate(limit: $limit, nextToken: $nextToken) {
      items {
        templateId
        title
        body
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// FAQエントリ一覧を取得するクエリ
export const LIST_FAQ_ENTRIES = gql`
  query ListFAQEntries($category: FAQCategory!, $limit: Int, $nextToken: String) {
    listFAQEntries(category: $category, limit: $limit, nextToken: $nextToken) {
      items {
        faqId
        category
        question
        answer
        sortOrder
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      nextToken
    }
  }
`;

// サーバー時間を取得する
export const GET_SERVER_TIME = gql`
  query GetServerTime {
    getServerTime
  }
`;

// CSVダウンロードリクエストを取得するクエリ（ステータスポーリング用）
export const GET_CSV_DOWNLOAD_REQUEST = gql`
  query GetCsvDownloadRequest($requestId: ID!) {
    getCsvDownloadRequest(requestId: $requestId) {
      requestId
      companyId
      requestType
      requestData
      status
      resultCsvPath
      fileName
      createdBy
      createdAt
      updatedAt
    }
  }
`;

// CSVダウンロードリクエスト一覧を取得するクエリ（ダウンロード履歴用）
export const LIST_CSV_DOWNLOAD_REQUESTS = gql`
  query ListCsvDownloadRequests($companyId: ID!, $limit: Int, $nextToken: String) {
    listCsvDownloadRequests(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        requestId
        companyId
        requestType
        requestData
        status
        resultCsvPath
        fileName
        createdBy
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// 資料を取得するクエリ
export const GET_MATERIAL = gql`
  query GetMaterial($materialId: ID!) {
    getMaterial(materialId: $materialId) {
      materialId
      companyId
      parentScenarioId
      name
      type
      pdfPath
      extractedText
      createdAt
      updatedAt
      createdBy
    }
  }
`;

// カスタム評価ルールを取得するクエリ
export const GET_CUSTOM_EVALUATION_RULE = gql`
  query GetCustomEvaluationRule($ruleId: ID!) {
    getCustomEvaluationRule(ruleId: $ruleId) {
      ruleId
      companyId
      parentScenarioId
      name
      content
      displayOrder
      createdAt
      updatedAt
      createdBy
    }
  }
`;

// カスタム評価ルール一覧を取得するクエリ
export const LIST_CUSTOM_EVALUATION_RULES = gql`
  query ListCustomEvaluationRules($parentScenarioId: ID!, $limit: Int, $nextToken: String) {
    listCustomEvaluationRules(parentScenarioId: $parentScenarioId, limit: $limit, nextToken: $nextToken) {
      items {
        ruleId
        companyId
        parentScenarioId
        name
        content
        displayOrder
        createdAt
        updatedAt
        createdBy
      }
      nextToken
    }
  }
`;
